import React, { useContext, useEffect, useState } from "react";
import { UserContext } from '../UserContext';
import { BuyStars, Manage, SubscriptionInfo } from '../components/Payments';
import { useTranslation } from "../LanguageContext";
import DonutChart, { SERIES_COLORS } from '../components/DonutChart';
import DepositModal from '../components/DepositModal';
import EarningsGoalCard from '../components/EarningsGoalCard';
import PendingRatings from '../components/PendingRatings';
import api from '../api';
import { publicProfileUrl } from '../utils/siteUrl';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

const usdShort = (value) => {
    const n = Number(value || 0);
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
    return `$${n.toFixed(0)}`;
};

/**
 * The page is a balanced two-column flow, not a grid of two fixed stacks —
 * cards are wildly different heights and several disappear entirely, so a
 * fixed split left one column towering over the other. Each card therefore
 * carries its own bottom gap and refuses to split across the break;
 * `space-y-*` cannot do that job because it does not cross a column boundary.
 * Cards that render `null` contribute nothing, which is exactly why the class
 * lives on each card's own root rather than on a wrapper around it.
 */
const CARD = 'break-inside-avoid mb-4 sm:mb-5';

function SubscriptionsSection() {
    const { user, setUser } = useContext(UserContext);
    const { t } = useTranslation();
    const subs = user.infs || [];
    const [loadingId, setLoadingId] = useState(null);
    const [toast, setToast] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3500);
    };

    const handleAction = async (sourceId, action) => {
        setLoadingId(sourceId);
        try {
            const res = await api.post(`/api/manage-subscription/`, { action, sub_id: sourceId });
            if (res.status === 200) {
                const updatedInfs = user.infs.map(inf => {
                    if (inf.subscription.source_id === sourceId) {
                        return {
                            ...inf,
                            subscription: {
                                ...inf.subscription,
                                cancel_at_period_end: res.data.cancel_at_period_end,
                            }
                        };
                    }
                    return inf;
                });
                setUser(prev => ({ ...prev, infs: updatedInfs }));
                showToast(res.data.message);
            }
        } catch (error) {
            const msg = error.response?.data?.error || 'An error occurred.';
            showToast(msg, 'error');
        } finally {
            setLoadingId(null);
        }
    };

    const getManageButton = (sub) => {
        const { subscription } = sub;
        const isLoading = loadingId === subscription.source_id;

        if (subscription.ended_at) {
            return (
                <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg flex-shrink-0">
                    {t('settings.subsExpired')}
                </span>
            );
        }

        if (subscription.cancel_at_period_end && subscription.status === 'active') {
            return (
                <button
                    type="button"
                    onClick={() => handleAction(sub.subscription.source_id, 'reactivate')}
                    disabled={isLoading}
                    className="text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg transition flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                    {isLoading ? (
                        <div className="w-3 h-3 border-2 border-emerald-300 border-t-emerald-600 rounded-full animate-spin"></div>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    )}
                    {t('settings.subsReactivate')}
                </button>
            );
        }

        if (subscription.status === 'active') {
            return (
                <button
                    type="button"
                    onClick={() => handleAction(sub.subscription.source_id, 'cancel')}
                    disabled={isLoading}
                    className="text-xs font-semibold text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 px-3 py-1.5 rounded-lg transition flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                >
                    {isLoading ? (
                        <div className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin"></div>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    )}
                    {t('settings.subsCancel')}
                </button>
            );
        }

        return (
            <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-lg flex-shrink-0">
                {t('settings.subsStatusInactive')}
            </span>
        );
    };

    if (subs.length === 0) return null;

    return (
        <section className={`rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#f9f9fc] relative ${CARD}`}>
            {toast && (
                <div className={`absolute top-3 right-3 z-10 px-4 py-2.5 rounded-xl shadow-lg text-xs font-semibold animate-fade-in ${toast.type === 'error' ? 'bg-red-600 text-white' : 'bg-emerald-600 text-white'
                    }`}>
                    {toast.message}
                </div>
            )}

            <h2 className="text-lg font-bold text-slate-800 mb-5 flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                {t('settings.subsTitle')}
                <span className="text-xs text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full font-medium ml-auto">{subs.length} {t('settings.subsTotal')}</span>
            </h2>

            <div className="space-y-3">
                {subs.map((sub) => (
                    <div key={sub.id} className={`flex items-center gap-3 p-3 bg-white rounded-xl border transition ${sub.subscription.cancel_at_period_end && !sub.subscription.ended_at
                        ? 'border-amber-200 bg-amber-50/30'
                        : sub.subscription.ended_at
                            ? 'border-slate-200 opacity-60'
                            : 'border-slate-100 hover:border-slate-200'
                        }`}>
                        <img
                            src={sub.inf_pp}
                            alt={sub.username}
                            className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                            <a href={publicProfileUrl(sub.username)} target="_blank" rel="noopener noreferrer">
                                <p className="text-sm font-semibold text-slate-800 truncate">{sub.username}</p>
                            </a>
                            <p className="text-xs text-slate-400">
                                {t('settings.subsStarted')} {formatDate(sub.subscription.started_at)}
                                {sub.subscription.cancel_at_period_end && !sub.subscription.ended_at && (
                                    <> · <span className="text-amber-600 font-medium">{t('settings.subsCancelsAtEnd')}</span></>
                                )}
                                {sub.subscription.ended_at && (
                                    <> · <span className="text-red-500">{t('settings.subsEnded')} {formatDate(sub.subscription.ended_at)}</span></>
                                )}
                            </p>
                        </div>
                        {getManageButton(sub)}
                    </div>
                ))}
            </div>
        </section>
    );
}

function CoffeesSection() {
    const { user } = useContext(UserContext);
    const { t } = useTranslation();
    const coffes = user.coffes || [];
    const [activeTab, setActiveTab] = useState('received');

    if (!user.coffes) return null;

    const receivedCoffees = coffes.filter(c => c.receiver === user.id);
    const sentCoffees = coffes.filter(c => c.receiver !== user.id);
    const displayCoffees = activeTab === 'received' ? receivedCoffees : sentCoffees;

    return (
        <section className={`rounded-xl sm:rounded-2xl border border-[#d2b48c] p-4 sm:p-5 bg-[#faf6f0] relative ${CARD}`}>
            <h2 className="text-lg font-bold text-[#6f4e37] mb-5 flex items-center gap-2">
                <img src="/coffee256.png" alt="Coffee" className="w-6 h-6 object-contain" onError={(e) => e.target.style.display = 'none'} />
                {t('finance.coffeesTitle')}
                <span className="text-xs text-[#8B5A2B] bg-[#f0e3d1] px-2.5 py-1 rounded-full font-bold ml-auto border border-[#d2b48c]/30">
                    {coffes.length} {t('finance.total')}
                </span>
            </h2>

            <div className="flex bg-[#f0e3d1] rounded-xl p-1 mb-4 shadow-inner">
                <button
                    className={`flex-1 text-sm font-bold py-2.5 rounded-lg transition-all duration-300 ${activeTab === 'received' ? 'bg-white text-[#6f4e37] shadow-sm scale-[1.02]' : 'text-[#8B5A2B] hover:bg-white/40'}`}
                    onClick={() => setActiveTab('received')}
                >
                    {t('finance.received')} ({receivedCoffees.length})
                </button>
                <button
                    className={`flex-1 text-sm font-bold py-2.5 rounded-lg transition-all duration-300 ${activeTab === 'sent' ? 'bg-white text-[#6f4e37] shadow-sm scale-[1.02]' : 'text-[#8B5A2B] hover:bg-white/40'}`}
                    onClick={() => setActiveTab('sent')}
                >
                    {t('finance.sent')} ({sentCoffees.length})
                </button>
            </div>

            {displayCoffees.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 opacity-60">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-2 text-[#8B5A2B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 12H4M12 20V4" />
                    </svg>
                    <p className="text-sm text-[#8B5A2B] font-medium">{t('finance.noCoffees')}</p>
                </div>
            ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                    {displayCoffees.map(coffee => (
                        <div key={coffee.id} className="bg-white p-3.5 sm:p-4 rounded-2xl border border-[#e8dccb] shadow-[0_2px_10px_rgba(111,78,55,0.05)] flex items-start gap-3.5 transition hover:shadow-[0_4px_15px_rgba(111,78,55,0.1)] hover:-translate-y-0.5 duration-300">
                            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#f4ebe1] to-[#e8dccb] flex items-center justify-center flex-shrink-0 shadow-inner border border-[#d2b48c]/50 relative">
                                <span className="text-lg font-black text-[#6f4e37] drop-shadow-sm flex items-center gap-0.5">
                                    {coffee.amount}
                                    <img src="/coffee256.png" alt="Coffee" className="w-4 h-4 object-contain drop-shadow-sm" />
                                </span>
                            </div>

                            <div className="flex-1 min-w-0 pt-0.5">
                                <div className="flex items-center justify-between mb-1.5 gap-2">
                                    <h4 className="font-bold text-[#4a3424] truncate text-[15px]">
                                        {activeTab === 'received'
                                            ? (coffee.name ? coffee.name : (coffee.email || t('finance.anonymous')))
                                            : t('finance.sentCoffee')}
                                    </h4>
                                    <span className="text-[10px] font-bold text-[#8B5A2B] bg-[#f4ebe1] border border-[#d2b48c]/30 px-2 py-1 rounded-md whitespace-nowrap shadow-sm">
                                        {formatDate(coffee.date)}
                                    </span>
                                </div>
                                {coffee.message ? (
                                    <p className="text-sm text-[#5c4a3d] bg-[#faf6f0] px-3 py-2 rounded-xl italic border-l-2 border-[#d2b48c] shadow-sm leading-relaxed">
                                        "{coffee.message}"
                                    </p>
                                ) : (
                                    <p className="text-xs text-[#8B5A2B]/50 italic font-medium mt-1">{t('finance.noMessage')}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/**
 * Balance hero: withdraw out, deposit in.
 *
 * The two amounts on this card are deliberately different pockets — the hero
 * figure is withdrawable earnings, the wallet strip below it is spendable
 * campaign money. Deposits fund the second and never the first, which is why
 * they are labelled rather than added together.
 */
function BalanceCard({ summary, onRequested, onDeposit }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [method, setMethod] = useState('bank');
    const [details, setDetails] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    const balance = summary.balance;
    const wallet = summary.wallet || {};
    // The payout threshold is no longer shown anywhere on this card — the
    // backend still enforces it, the creator is simply not told the figure.
    // const minimum = summary.min_payout_usd;
    // const progress = Math.min(100, (balance.available_usd / minimum) * 100);
    const pendingPayout = (summary.payouts || []).find(p => p.status === 'pending');

    const request = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await api.post('/api/finance/payout/', { method, account_details: details });
            if (res.status === 201) { setOpen(false); setDetails(''); onRequested(); }
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === 'below_minimum' ? t('finance.belowMinimum')
                : code === 'already_pending' ? t('finance.alreadyPending')
                    : t('finance.payoutError'));
        } finally { setBusy(false); }
    };

    return (
        <section className={`rounded-2xl overflow-hidden border border-primary-200 bg-gradient-to-br from-primary-600 to-primary-800 text-white ${CARD}`}>
            <div className="p-5">
                <p className="text-xs font-semibold text-white/70 uppercase tracking-wide">{t('finance.availableBalance')}</p>
                <p className="text-4xl font-black mt-1 tracking-tight">{usd(balance.available_usd)}</p>

                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-white/80">
                    <span>{t('finance.pending')}: <strong className="text-white">{usd(balance.pending_usd)}</strong></span>
                    <span>{t('finance.lifetimeNet')}: <strong className="text-white">{usd(balance.lifetime_net_usd)}</strong></span>
                    <span>{t('finance.withdrawn')}: <strong className="text-white">{usd(balance.withdrawn_usd)}</strong></span>
                </div>

                {/* Progress toward the payout threshold — hidden on purpose.
                {!balance.can_withdraw && (
                    <div className="mt-4">
                        <div className="flex justify-between text-[11px] text-white/70 mb-1">
                            <span>{t('finance.payoutProgress')}</span>
                            <span>{usd(balance.available_usd)} / {usd(minimum)}</span>
                        </div>
                        <div className="h-2 rounded-full bg-white/20 overflow-hidden">
                            <div className="h-full rounded-full bg-white transition-all duration-500"
                                style={{ width: `${progress}%` }} />
                        </div>
                    </div>
                )} */}

                {/* Spendable campaign money — a different pocket, so it gets
                    its own row rather than being folded into the figure above. */}
                <div className="mt-4 rounded-xl bg-white/10 border border-white/15 px-3.5 py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                        <p className="text-[11px] font-semibold text-white/70 uppercase tracking-wide">
                            {t('wallet.spendableBalance')}
                        </p>
                        <p className="text-xl font-black tracking-tight">{usd(wallet.balance_usd)}</p>
                        {wallet.escrow_outgoing_usd > 0 && (
                            <p className="text-[11px] text-white/60 mt-0.5">
                                {t('wallet.inEscrow')}: {usd(wallet.escrow_outgoing_usd)}
                            </p>
                        )}
                    </div>
                    <button onClick={onDeposit}
                        className="flex-shrink-0 px-4 py-2.5 rounded-xl text-sm font-bold bg-white text-primary-700 hover:bg-primary-50 transition flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                        {t('wallet.depositBtn')}
                    </button>
                </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm px-5 py-4 border-t border-white/10">
                {pendingPayout ? (
                    <div className="text-center">
                        <p className="text-sm font-bold">{t('finance.payoutPending')}</p>
                        <p className="text-xs text-white/70 mt-0.5">
                            {usd(pendingPayout.amount_usd)} · {formatDate(pendingPayout.created_at)}
                        </p>
                    </div>
                ) : open ? (
                    <div className="space-y-2">
                        <select className="input py-2 text-sm text-slate-800" value={method}
                            onChange={(e) => setMethod(e.target.value)}>
                            <option value="bank">{t('finance.methodBank')}</option>
                            <option value="wise">Wise</option>
                            <option value="paypal">PayPal</option>
                        </select>
                        <input className="input py-2 text-sm text-slate-800" value={details}
                            placeholder={t('finance.accountPlaceholder')}
                            onChange={(e) => setDetails(e.target.value)} />
                        {error && <p className="text-xs text-red-200">{error}</p>}
                        <div className="flex gap-2">
                            <button onClick={() => setOpen(false)}
                                className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-white/20 hover:bg-white/30 transition">
                                {t('campaigns.cancel')}
                            </button>
                            <button onClick={request} disabled={busy}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold bg-white text-primary-700 hover:bg-primary-50 transition flex items-center justify-center gap-2">
                                {busy && <span className="w-4 h-4 border-2 border-primary-200 border-t-primary-600 rounded-full animate-spin" />}
                                {t('finance.confirmPayout')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        <button
                            onClick={() => setOpen(true)}
                            disabled={!balance.can_withdraw}
                            className={`w-full py-3 rounded-xl text-base font-bold transition ${balance.can_withdraw
                                ? 'bg-white text-primary-700 hover:bg-primary-50 shadow-sm'
                                : 'bg-white/20 text-white/50 cursor-not-allowed'}`}
                        >
                            {t('finance.withdrawBtn')}
                        </button>
                        {/* Minimum-payout note — hidden on purpose.
                        {!balance.can_withdraw && (
                            <p className="text-[11px] text-white/60 text-center mt-2 leading-relaxed">
                                {t('finance.minimumNote')} {usd(minimum)}
                            </p>
                        )} */}
                    </>
                )}
            </div>
        </section>
    );
}

function BreakdownCard({ summary }) {
    const { t } = useTranslation();
    const rows = summary.breakdown || [];
    const [mode, setMode] = useState('net');   // net | gross

    const data = rows.map(row => ({
        key: row.source,
        label: t(`finance.source_${row.source}`),
        value: mode === 'net' ? row.net_usd : row.gross_usd,
    }));

    const totals = rows.reduce((acc, row) => ({
        gross: acc.gross + row.gross_usd,
        fee: acc.fee + row.fee_usd,
        net: acc.net + row.net_usd,
    }), { gross: 0, fee: 0, net: 0 });

    return (
        <section className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${CARD}`}>
            <div className="flex items-center justify-between gap-3 mb-1">
                <h2 className="text-lg font-bold text-slate-800">{t('finance.breakdownTitle')}</h2>
                <div className="flex bg-slate-100 rounded-lg p-0.5">
                    {['net', 'gross'].map(key => (
                        <button key={key} onClick={() => setMode(key)}
                            className={`px-2.5 py-1 rounded-md text-[11px] font-bold transition ${mode === key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
                            {t(`finance.${key}`)}
                        </button>
                    ))}
                </div>
            </div>
            <p className="text-xs text-slate-400 mb-4">{t('finance.breakdownHint')}</p>

            <div className="flex flex-col sm:flex-row items-center gap-5">
                <DonutChart
                    data={data}
                    centerLabel={t(`finance.${mode}`)}
                    format={usdShort}
                    size={190}
                />

                {/* Legend — identity is never carried by color alone */}
                <div className="flex-1 w-full space-y-1.5">
                    {rows.map(row => {
                        const value = mode === 'net' ? row.net_usd : row.gross_usd;
                        const share = totals[mode] > 0 ? (value / totals[mode]) * 100 : 0;
                        return (
                            <div key={row.source} className="flex items-center gap-2.5">
                                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                                    style={{ backgroundColor: SERIES_COLORS[row.source] }} />
                                <span className="text-xs text-slate-600 flex-1 min-w-0 truncate">
                                    {t(`finance.source_${row.source}`)}
                                </span>
                                <span className="text-xs font-bold text-slate-800 tabular-nums">{usd(value)}</span>
                                <span className="text-[10px] text-slate-400 w-9 text-right tabular-nums">
                                    {share ? `${Math.round(share)}%` : '—'}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Exact values — also the relief for the low-contrast slot */}
            <div className="mt-5 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="text-slate-400 border-b border-slate-100">
                            <th className="text-left font-semibold py-2">{t('finance.source')}</th>
                            <th className="text-right font-semibold py-2">{t('finance.gross')}</th>
                            <th className="text-right font-semibold py-2">{t('finance.fee')}</th>
                            <th className="text-right font-semibold py-2">{t('finance.net')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(row => (
                            <tr key={row.source} className="border-b border-slate-50">
                                <td className="py-2 text-slate-700 font-medium flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-sm flex-shrink-0"
                                        style={{ backgroundColor: SERIES_COLORS[row.source] }} />
                                    {t(`finance.source_${row.source}`)}
                                </td>
                                <td className="py-2 text-right text-slate-600 tabular-nums">{usd(row.gross_usd)}</td>
                                <td className="py-2 text-right text-slate-400 tabular-nums">−{usd(row.fee_usd)}</td>
                                <td className="py-2 text-right text-slate-900 font-bold tabular-nums">{usd(row.net_usd)}</td>
                            </tr>
                        ))}
                        <tr className="font-bold">
                            <td className="py-2 text-slate-800">{t('finance.total')}</td>
                            <td className="py-2 text-right text-slate-700 tabular-nums">{usd(totals.gross)}</td>
                            <td className="py-2 text-right text-slate-400 tabular-nums">−{usd(totals.fee)}</td>
                            <td className="py-2 text-right text-slate-900 tabular-nums">{usd(totals.net)}</td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Fees & tax */}
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-3 space-y-1.5">
                <div className="flex justify-between text-xs">
                    <span className="text-slate-500">{t('finance.platformFee')} (%{summary.fee_percent})</span>
                    <span className="font-semibold text-slate-700 tabular-nums">−{usd(summary.balance.lifetime_fee_usd)}</span>
                </div>
                <div className="flex justify-between text-xs">
                    <span className="text-slate-500">{t('finance.withholding')} (%{summary.tax_percent})</span>
                    <span className="font-semibold text-slate-700 tabular-nums">−{usd(summary.balance.tax_estimate_usd)}</span>
                </div>
                <div className="flex justify-between text-sm pt-1.5 border-t border-slate-200">
                    <span className="font-bold text-slate-700">{t('finance.payable')}</span>
                    <span className="font-black text-slate-900 tabular-nums">{usd(summary.balance.payable_usd)}</span>
                </div>
                <p className="text-[10px] text-slate-400 leading-relaxed pt-1">{t('finance.taxNote')}</p>
            </div>
        </section>
    );
}

function TrendCard({ monthly }) {
    const { t } = useTranslation();
    const peak = Math.max(...monthly.map(m => m.net_usd), 1);

    return (
        <section className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${CARD}`}>
            <h2 className="text-lg font-bold text-slate-800">{t('finance.trendTitle')}</h2>
            <p className="text-xs text-slate-400 mb-4">{t('finance.trendHint')}</p>
            <div className="flex items-end justify-between gap-2 h-28">
                {monthly.map(month => (
                    <div key={month.month} className="flex-1 flex flex-col items-center justify-end h-full group">
                        <span className="text-[9px] font-bold text-slate-500 mb-1 opacity-0 group-hover:opacity-100 transition tabular-nums">
                            {usdShort(month.net_usd)}
                        </span>
                        <div className="w-full rounded-t-[4px] bg-primary-500 hover:bg-primary-600 transition-colors"
                            style={{ height: `${Math.max(2, (month.net_usd / peak) * 100)}%` }}
                            title={`${month.month}: ${usd(month.net_usd)}`} />
                        <span className="text-[9px] text-slate-400 mt-1.5 tabular-nums">{month.month.slice(5)}</span>
                    </div>
                ))}
            </div>
        </section>
    );
}

/** Campaign money: what is spendable, locked and lifetime funded. */
function WalletCard({ summary, onDeposit }) {
    const { t } = useTranslation();
    const wallet = summary.wallet || {};
    const rows = summary.wallet_transactions || [];

    const KIND_TONE = {
        deposit: 'text-emerald-600',
        escrow_refund: 'text-emerald-600',
        escrow_lock: 'text-slate-500',
        admin: 'text-violet-600',
    };

    return (
        <section className={`rounded-2xl border border-primary-200 bg-white p-4 sm:p-5 ${CARD}`}>
            <div className="flex items-start justify-between gap-3 mb-1">
                <h2 className="text-lg font-bold text-slate-800">{t('wallet.cardTitle')}</h2>
                <button onClick={onDeposit}
                    className="btn-primary px-3.5 py-2 text-xs font-bold flex-shrink-0">
                    + {t('wallet.depositBtn')}
                </button>
            </div>
            <p className="text-xs text-slate-400 mb-4 leading-relaxed">{t('wallet.cardHint')}</p>

            <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-primary-50 border border-primary-100 p-3 text-center">
                    <p className="text-lg font-black text-primary-700 tabular-nums">{usd(wallet.balance_usd)}</p>
                    <p className="text-[10px] text-slate-500 font-semibold mt-0.5">{t('wallet.spendable')}</p>
                </div>
                <div className="rounded-xl bg-white border border-slate-200 p-3 text-center">
                    <p className="text-lg font-black text-amber-600 tabular-nums">{usd(wallet.escrow_outgoing_usd)}</p>
                    <p className="text-[10px] text-slate-500 font-semibold mt-0.5">{t('wallet.locked')}</p>
                </div>
                <div className="rounded-xl bg-white border border-slate-200 p-3 text-center">
                    <p className="text-lg font-black text-slate-600 tabular-nums">{usd(wallet.deposited_usd)}</p>
                    <p className="text-[10px] text-slate-500 font-semibold mt-0.5">{t('wallet.deposited')}</p>
                </div>
            </div>

            {rows.length > 0 && (
                <div className="mt-4">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                        {t('wallet.history')}
                    </p>
                    <div className="max-h-56 overflow-y-auto custom-scrollbar pr-1">
                        {rows.map(row => (
                            <div key={row.id} className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0">
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold text-slate-700 truncate">
                                        {t(`wallet.kind_${row.kind}`)}
                                        {row.counterparty && <span className="text-slate-400"> · {row.counterparty}</span>}
                                    </p>
                                    <p className="text-[10px] text-slate-400">{formatDate(row.created_at)}</p>
                                </div>
                                <span className={`text-xs font-black tabular-nums whitespace-nowrap ${KIND_TONE[row.kind] || 'text-slate-600'}`}>
                                    {row.amount_usd < 0 ? '−' : '+'}{usd(Math.abs(row.amount_usd))}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
}

/** Stars: chat gifting only. They fund nothing else. */
function StarWalletCard({ summary }) {
    const { t } = useTranslation();
    const stars = summary.stars;

    return (
        <section className={`rounded-2xl border border-[#20c9b5]/30 bg-gradient-to-br from-[#e6fcf9] to-white p-4 sm:p-5 ${CARD}`}>
            {/* The balance sits in the header, not in a tile of its own — the
                purchase form below already sits under it, and showing the same
                number twice on one card was just noise. */}
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <img src="/star192.png" alt="" className="w-5 h-5 object-contain" />
                    {t('finance.starWallet')}
                </h2>
                <span className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full border border-[#20c9b5]/30 flex-shrink-0">
                    <img src="/star192.png" alt="" className="w-4 h-4 object-contain" />
                    <span className="text-base font-black text-[#0f8a7e] tabular-nums">{stars.balance}</span>
                </span>
            </div>
            <p className="text-xs text-slate-400 mt-1 mb-4 leading-relaxed">
                {t('finance.starPurposeNote')} · 1 ★ = {usd(summary.star_rate_usd)} {t('finance.onPayout')}
            </p>

            {/* Yıldız satın alma serbest miktar girişiyle burada, cüzdanın içinde. */}
            <BuyStars />
        </section>
    );
}

function PendingPaymentsCard({ payments }) {
    const { t } = useTranslation();
    if (!payments?.length) return null;

    return (
        <section className={`rounded-2xl border border-amber-200 bg-amber-50/40 p-4 sm:p-5 ${CARD}`}>
            <h2 className="text-lg font-bold text-slate-800">{t('finance.pendingPaymentsTitle')}</h2>
            <p className="text-xs text-slate-500 mb-4">{t('finance.pendingPaymentsHint')}</p>
            <div className="space-y-2">
                {payments.map(payment => (
                    <div key={payment.id} className="flex items-center gap-3 bg-white rounded-xl border border-amber-100 p-3">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800 truncate">{payment.campaign}</p>
                            <p className="text-[11px] text-slate-400">
                                {payment.counterparty} · {t(`campaigns.dealStatus_${payment.status}`)}
                            </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <p className="text-sm font-black text-slate-800 tabular-nums">{usd(payment.usd)}</p>
                            <p className="text-[10px] text-slate-400">
                                {t(`campaigns.escrow_${payment.escrow_status}`)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}

function PayoutHistoryCard({ payouts }) {
    const { t } = useTranslation();
    if (!payouts?.length) return null;

    const styles = {
        pending: 'bg-amber-50 text-amber-700 border-amber-200',
        approved: 'bg-sky-50 text-sky-700 border-sky-200',
        paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        rejected: 'bg-red-50 text-red-600 border-red-200',
    };

    return (
        <section className={`rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 ${CARD}`}>
            <h2 className="text-lg font-bold text-slate-800 mb-4">{t('finance.payoutHistory')}</h2>
            <div className="space-y-2">
                {payouts.map(payout => (
                    <div key={payout.id} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800">{usd(payout.amount_usd)}</p>
                            <p className="text-[11px] text-slate-400">
                                {formatDate(payout.created_at)} · {t(`finance.method${payout.method.charAt(0).toUpperCase() + payout.method.slice(1)}`)}
                            </p>
                        </div>
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border ${styles[payout.status]}`}>
                            {t(`finance.payoutStatus_${payout.status}`)}
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
}

function Finance() {
    const { user } = useContext(UserContext);
    const { t } = useTranslation();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [depositing, setDepositing] = useState(false);

    const load = async () => {
        try {
            const res = await api.get('/api/finance/summary/');
            setSummary(res.data);
        } catch (err) { /* the interceptor surfaces auth/payment errors */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    if (loading) {
        return <div className="flex items-center justify-center h-screen"><div className="spinner" /></div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-28">
            <div className="max-w-6xl mx-auto px-3 sm:px-4 pt-4 sm:pt-6">

                <div className="mb-4">
                    <h1 className="text-xl sm:text-2xl font-black text-slate-900">{t('finance.pageTitle')}</h1>
                    <p className="text-xs text-slate-500">{t('finance.pageSubtitle')}</p>
                </div>

                {/* One balanced flow rather than two fixed stacks: the browser
                    keeps both columns the same height whatever renders, so a
                    card no longer waits for the tall column to end before it
                    can appear. */}
                <div className="columns-1 lg:columns-2 gap-4 sm:gap-5">
                    {summary && (
                        <>
                            <BalanceCard summary={summary} onRequested={load}
                                onDeposit={() => setDepositing(true)} />
                            {/* Momentum sits directly under the balance: the
                                total answers "how much", this answers "how is
                                it going", and the second is why anyone opens
                                the page twice in a month. */}
                            <EarningsGoalCard pace={summary.pace} className={CARD} />
                            <PendingRatings className={CARD} />
                            <WalletCard summary={summary} onDeposit={() => setDepositing(true)} />
                            <StarWalletCard summary={summary} />
                            <PendingPaymentsCard payments={summary.pending_payments} />
                            <PayoutHistoryCard payouts={summary.payouts} />
                            <BreakdownCard summary={summary} />
                            <TrendCard monthly={summary.monthly || []} />
                        </>
                    )}

                    <SubscriptionsSection />

                    <CoffeesSection />

                    {!user.trial && (
                        <section className={`rounded-xl sm:rounded-2xl border border-[#c0c0c0] p-4 sm:p-5 bg-[#f9f9fc] ${CARD}`}>
                            <h2 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                </svg>
                                {t('settings.tabPayments')}
                            </h2>
                            <Manage />
                            {user.subscription.started_at && <SubscriptionInfo />}
                        </section>
                    )}
                </div>
            </div>

            {depositing && (
                <DepositModal
                    minDeposit={summary?.min_deposit_usd ?? 5}
                    maxDeposit={summary?.max_deposit_usd ?? 50000}
                    onClose={() => { setDepositing(false); load(); }}
                    onFunded={load}
                />
            )}
        </div>
    );
}

export default Finance;
